import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import QRCode from "qrcode";
import { FaDownload, FaExclamationCircle, FaCheckCircle, FaTimes, FaSync, FaWallet, FaCreditCard } from "react-icons/fa";
// import signature from "../../../public/assets/signature.png";
// import html2pdf from "html2pdf.js";

const ReporterGetIdCard = () => {
  const navigate = useNavigate();
  const [idCardData, setIdCardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIdCard, setShowIdCard] = useState(false);
  const [message, setMessage] = useState("");
  const [rejectedNote, setRejectedNote] = useState(null); // Add this state
  const [qrDataUrl, setQrDataUrl] = useState(null); // Pre-generated QR image for PDF
  const [isFlipped, setIsFlipped] = useState(false); // State for mobile flip

  const [userRole, setUserRole] = useState("Reporter");

  // Get user role from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.role) {
      setUserRole(user.role);
    }
  }, []);

  // Pre-generate QR code as PNG data URL whenever idCardData changes
  useEffect(() => {
    if (idCardData?.data?._id) {
      const qrValue = `${process.env.REACT_APP_API_BASE_URL || 'https://iinsaf.com'}/reporter/view/${idCardData.data._id}`;
      QRCode.toDataURL(qrValue, {
        width: 300,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      }).then(url => setQrDataUrl(url)).catch(console.error);
    }
  }, [idCardData]);

  const handleGetIdCard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/icard`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Response from get ID card:", response);

      if (response.status === 201) {
        setIdCardData(response.data);
        setShowIdCard(true);
      } else if (response.status === 200) {
        if (response.data.message) {
          setMessage(response.data.message);
          // Show rejectedNote if present
          if (response.data.rejectedNote) {
            setRejectedNote(response.data.rejectedNote);
          } else {
            setRejectedNote(null);
          }
        }
      } else {
        toast.error("You are not eligible for this ID card");
      }
    } catch (error) {
      console.error("Error fetching ID card:", error);

      // ✅ Read the 404 or custom status from Axios error response
      if (error.response && error.response.status === 404) {
        setMessage("You are not verified yet. Please create your ID card");
        toast.info("You are not verified yet. Please create your ID card");
      } else {
        toast.error("Failed to fetch ID card. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadIdCards = async () => {
    const { toPng } = await import("html-to-image");
    const jsPDF = (await import("jspdf")).jsPDF;

    const front = document.getElementById("card-front");
    const back = document.getElementById("card-back");

    if (!front || !back) {
      toast.error("Card elements not found. Please wait for cards to load.");
      return;
    }

    toast.info("Generating PDF...", { autoClose: 3000 });
    await document.fonts.ready;

    try {
      const captureOptions = {
        quality: 1.0,
        pixelRatio: 3,           // High resolution
        backgroundColor: "#ffffff",
        cacheBust: true,
        skipFonts: false,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          margin: 0,
        },
        width: 400,
        height: 770,
      };

      // Temporarily ensure both cards are fully visible to capture them
      const originalFrontDisplay = front.parentElement.style.display;
      const originalBackDisplay = back.parentElement.style.display;
      front.parentElement.style.display = 'block';
      back.parentElement.style.display = 'block';

      // Capture front card as PNG
      const frontPng = await toPng(front, captureOptions);

      // Capture back card as PNG
      const backPng = await toPng(back, captureOptions);

      // Restore states
      front.parentElement.style.display = originalFrontDisplay;
      back.parentElement.style.display = originalBackDisplay;

      const pdf = new jsPDF("portrait", "mm", [82, 158]);

      pdf.addImage(frontPng, "PNG", 0, 0, 82, 158);
      pdf.addPage([82, 158], "portrait");
      pdf.addImage(backPng, "PNG", 0, 0, 82, 158);

      const filename = userRole === "Influencer" ? "INFLUENCER_ID_Card.pdf" : "IINSAF_ID_Card.pdf";
      pdf.save(filename);
      toast.success("ID Cards downloaded successfully!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  // Handle navigation to cards page
  const handleNavigateToCards = () => {
    setMessage("");
    setRejectedNote(null);
    navigate("/reporter/cards");
  };

  useEffect(() => {
    handleGetIdCard();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="reporter-id-card-container">
      {/* Professional Status View */}
      {message && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] max-w-[360px] w-full border border-white/40 overflow-hidden transform animate-scaleUp">

            {/* Minimalist Header Area */}
            <div className={`p-1.5 ${rejectedNote ? 'bg-rose-500' : 'bg-indigo-600'}`}>
              <div className="flex items-center justify-between px-4 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
                    {rejectedNote ? (
                      <FaExclamationCircle className="w-3 h-3 text-white" />
                    ) : (
                      <FaCheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    {rejectedNote ? "Review Notification" : "System Message"}
                  </span>
                </div>
                <button
                  onClick={handleNavigateToCards}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <FaTimes className="w-3 h-3 text-white" />
                </button>
              </div>
            </div>

            {/* Content Body */}
            <div className="p-8 text-center">
              {/* Illustration Area */}
              <div className="relative inline-block mb-6">
                <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center mx-auto ${rejectedNote ? 'bg-rose-50 text-rose-500 shadow-rose-100' : 'bg-indigo-50 text-indigo-600 shadow-indigo-100'
                  } shadow-xl border-4 border-white`}>
                  {rejectedNote ? (
                    <FaExclamationCircle className="w-8 h-8" />
                  ) : (
                    <div className="relative">
                      <FaSync className="w-8 h-8 animate-spin-slow opacity-20 absolute inset-0" />
                      <FaWallet className="w-8 h-8 relative z-10" />
                    </div>
                  )}
                </div>
                {!rejectedNote && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-400 rounded-full border-2 border-white flex items-center justify-center shadow-sm">
                    <span className="text-[10px] animate-pulse">⏳</span>
                  </div>
                )}
              </div>

              <h3 className={`text-xl font-black mb-3 tracking-tight ${rejectedNote ? 'text-rose-600' : 'text-slate-900'}`}>
                {rejectedNote ? 'Action Required' : 'Under Review'}
              </h3>

              <p className="text-slate-500 text-xs font-bold leading-relaxed mb-6 px-4">
                {message}
              </p>

              {/* Functional Information Area */}
              {rejectedNote ? (
                <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-4 mb-8 text-left relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-16 h-16 bg-rose-500/5 rounded-full -mr-8 -mt-8" />
                  <h4 className="text-[10px] font-black text-rose-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                    <FaExclamationCircle className="w-3 h-3" /> Review Feedback
                  </h4>
                  <p className="text-xs font-bold text-rose-700 leading-relaxed">
                    {rejectedNote}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="px-2 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full uppercase">Wait 24h</div>
                    <span className="text-[9px] font-bold text-rose-400 uppercase tracking-tight">to re-apply</span>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 mb-8 text-left group">
                  <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                    <FaSync className="w-3 h-3 group-hover:rotate-180 transition-transform duration-700" /> Professional Audit
                  </h4>
                  <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                    Our verification team is carefully auditing your identity. You'll be notified immediately once your official Press ID is ready.
                  </p>
                </div>
              )}

              {/* Footer Action */}
              <div className="flex items-center justify-between gap-4">
                <div className="text-left">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Support Required?</p>
                  <p className="text-[10px] font-black text-indigo-600 uppercase tracking-tight hover:underline cursor-pointer">Help Center</p>
                </div>
                <button
                  onClick={handleNavigateToCards}
                  className={`flex-1 py-3.5 rounded-2xl text-white font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg ${rejectedNote ? 'bg-rose-500 shadow-rose-200 hover:bg-rose-600' : 'bg-slate-900 shadow-slate-200 hover:bg-slate-800'
                    }`}
                >
                  {rejectedNote ? 'Understood' : 'Okay, Got it'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div className="bg-white/95 backdrop-blur-xl p-10 rounded-[2.5rem] shadow-2xl border border-white text-center max-w-sm w-full animate-pulse">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FaSync className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
            <h2 className="text-xl font-black text-slate-900 mb-2">Authenticating</h2>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Retrieving Official Credentials</p>
          </div>
        </div>
      ) : showIdCard ? (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
          {/* Compact Professional Header */}
          <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-4 py-3 sm:py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div className="text-left">
                    <h1 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">
                      Press <span className="text-blue-600">ID Cards</span>
                    </h1>
                    <p className="text-[10px] sm:text-xs text-slate-500 font-bold uppercase tracking-widest">Verified IINSAF Identity</p>
                  </div>
                </div>

                {/* Compact Download Button */}
                <button
                  onClick={downloadIdCards}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-blue-600 text-white font-black py-3 px-6 rounded-xl shadow-xl transition-all active:scale-95 text-sm uppercase tracking-wider"
                >
                  <FaDownload className="w-4 h-4" />
                  Download Cards
                </button>
              </div>

              {/* Mobile Flip Toggle Button */}
              <div className="mt-6 flex justify-center xl:hidden">
                <div className="bg-slate-100 p-1 rounded-xl flex items-center shadow-inner relative w-64 max-w-full">
                  <div className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-sm transition-transform duration-300 ${isFlipped ? 'translate-x-full left-1' : 'translate-x-0 left-1'}`}></div>
                  <button
                    onClick={() => setIsFlipped(false)}
                    className={`flex-1 py-2.5 text-xs font-black uppercase tracking-widest relative z-10 transition-colors ${!isFlipped ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Front Side
                  </button>
                  <button
                    onClick={() => setIsFlipped(true)}
                    className={`flex-1 py-2.5 text-xs font-black uppercase tracking-widest relative z-10 transition-colors ${isFlipped ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    Back Side
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Compact ID Cards Container */}
          <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-10">
            <div id="id-cards-container" className="bg-white/50 backdrop-blur-sm rounded-[2.5rem] shadow-2xl border border-white p-2 sm:p-10 flex flex-col items-center">
              {/* Map through reporterIdCards */}
              {[idCardData].map((card, index) => (
                <div
                  key={card._id || index}
                  className="flex flex-col xl:flex-row justify-center items-center gap-0 sm:gap-6 xl:gap-12 w-full perspective-[1000px]"
                >
                  {/* Card Front - Scaled for mobile */}
                  <div className={`scale-[0.55] sm:scale-[0.65] md:scale-[0.70] xl:scale-[0.75] 2xl:scale-[0.80] origin-top h-[430px] sm:h-[510px] md:h-[550px] xl:h-[580px] 2xl:h-[620px] overflow-visible transition-all duration-500 ${isFlipped ? 'hidden xl:block' : 'block xl:block'}`}>
                    <div
                      id="card-front"
                      className="w-[400px] h-[770px] bg-white flex shadow-2xl rounded-2xl overflow-hidden border border-slate-100"
                    >
                      {/* Vertical Red Section */}
                      <div className="bg-red-600 w-[55px] flex items-center justify-center text-white text-[24px] text-center font-bold relative">
                        <span className="rotate-90 whitespace-nowrap text-[32px] font-bold tracking-[0.6em] leading-none">
                          RAISE YOUR VOICE
                        </span>
                      </div>

                      {/* Main Card Content */}
                      <div className="bg-white p-4 relative flex-1 min-h-[770px] h-full">
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
                          <img src="/images/iinsaf.png" className="w-full h-full object-contain" alt="" />
                        </div>

                        <div className="flex flex-col items-center mb-4">
                          <img src="/images/iinsaf.png" alt="IINSAF Logo" className="h-14 mb-1" />
                          <div className="text-center px-4">
                            <p className="text-[10px] font-black text-slate-800 uppercase leading-tight tracking-tight">
                              <span className="text-red-600">International Influencing</span><br />
                              News Social Media Advertisement Federation
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-center mb-2">
                          <img src="/assets/presslog.png" alt="Press" className="h-12 object-contain" />
                        </div>

                        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border border-slate-700/50 text-white text-center py-2.5 rounded-xl mb-6 mx-5 shadow-lg shadow-slate-900/20">
                          <p className="text-[10px] sm:text-xs font-black tracking-[0.2em] text-indigo-200 mb-0.5 uppercase drop-shadow-sm">
                            {userRole === "Influencer" ? "INFLUENCER ID" : "IINSAF PRESS ID"}
                          </p>
                          <p className="text-xl sm:text-2xl font-black font-mono tracking-tight drop-shadow-md">{card.data.iinsafId}</p>
                        </div>

                        {/* Photo Section */}
                        <div className="flex justify-center mb-6">
                          <div className="w-32 h-40 rounded-2xl overflow-hidden border-[5px] border-white shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
                            <img src={card.data.image || "/images/placeholder.png"} className="w-full h-full object-cover" alt="User" />
                          </div>
                        </div>

                        {/* Details Section */}
                        <div className="px-6 space-y-3.5 pb-24">
                          {[
                            { label: 'Name', value: card.data.name },
                            { label: 'Phone', value: card.data.mobileNo },
                            { label: 'DOB', value: card.data.dateOfBirth },
                            { label: 'Blood Type', value: card.data.bloodGroup, color: 'text-red-600 font-extrabold' },
                            { label: 'Issue Date', value: card.data.issuedDate },
                            { label: 'Valid Up To', value: card.data.validUpto, color: 'text-emerald-600 font-extrabold' }
                          ].map((item, i) => (
                            <div key={i} className="flex justify-between items-end border-b border-slate-100 pb-1">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.label}</span>
                              <span className={`text-sm sm:text-base font-black text-slate-900 lowercase capitalize ${item.color || ''}`}>{item.value}</span>
                            </div>
                          ))}
                        </div>

                        {/* Authority Signature */}
                        <div className="absolute bottom-6 right-8 text-right w-40">
                          <div className="relative h-16 mb-1">
                            <img
                              src="/assets/signature.png"
                              className="w-full h-full object-contain absolute bottom-0 right-0 rotate-[-8deg] z-10 translate-y-3 opacity-90 scale-125 origin-bottom-right"
                              alt="Signature"
                            />
                            <div className="absolute bottom-0 right-0 w-full h-[2px] bg-slate-200"></div>
                          </div>
                          <p className="text-[10px] font-black text-slate-900 uppercase tracking-tight leading-none pt-1">IINSAF Authority</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Back - Scaled for mobile */}
                  <div className={`scale-[0.55] sm:scale-[0.65] md:scale-[0.70] xl:scale-[0.75] 2xl:scale-[0.80] origin-top h-[430px] sm:h-[510px] md:h-[550px] xl:h-[580px] 2xl:h-[620px] overflow-visible transition-all duration-500 ${!isFlipped ? 'hidden xl:block' : 'block xl:block'}`}>
                    <div
                      id="card-back"
                      className="w-[400px] h-[770px] bg-white flex shadow-2xl rounded-2xl overflow-hidden border border-slate-100"
                    >
                      {/* Vertical Red Section */}
                      <div className="bg-red-600 w-[55px] flex items-center justify-center text-black text-[24px] text-center font-bold relative">
                        <span className="rotate-90 whitespace-nowrap text-[36px] font-black tracking-[1.5em] leading-none uppercase">
                          iinsaf
                        </span>
                      </div>

                      <div className="bg-slate-50 p-4 relative flex-1 flex flex-col">
                        <div className="bg-white rounded-2xl p-6 flex-1 shadow-inner border border-white">
                          <div className="flex flex-col items-center mb-6">
                            <img src="/CompanyImages/insaaf_logo-1.png" className="h-28 object-contain mb-4" alt="Logo" />
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Official Channels</h4>

                            <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-md flex items-center gap-4 w-full">
                              <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg border-2 border-slate-50">
                                <img src={card.data.channelLogo || "/images/placeholder.png"} className="w-full h-full object-cover" alt="Channel" />
                              </div>
                              <div className="flex-1">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Channel Name</p>
                                <p className="text-sm font-black text-slate-900">{card.data.channelName}</p>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-5 px-3">
                            {[
                              { label: 'Aadhar No', value: card.data.aadharNo },
                              { label: 'Address', value: card.data.ResidentialAddress },
                              { label: 'Location', value: `${card.data.city}, ${card.data.state}` }
                            ].map((item, i) => (
                              <div key={i} className="space-y-1.5 border-b border-slate-100 pb-2">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.label}</span>
                                <p className="text-sm sm:text-base font-black text-slate-900 leading-tight">{item.value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Fixed QR Code Section */}
                          <div className="pt-6 pb-10 text-center">
                            <div className="inline-block p-4 bg-white rounded-3xl shadow-xl border border-slate-50 mb-3 hover:scale-105 transition-transform duration-500">
                              {/* QR as plain <img> — always captured by html2canvas */}
                              {qrDataUrl ? (
                                <img
                                  src={qrDataUrl}
                                  alt="QR Code"
                                  width={120}
                                  height={120}
                                  style={{ imageRendering: 'pixelated' }}
                                />
                              ) : (
                                <div className="w-[120px] h-[120px] flex items-center justify-center text-slate-300 text-xs">Loading QR...</div>
                              )}
                            </div>
                            <p className="text-xs font-black text-slate-900 tracking-widest uppercase">www.iinsaf.com</p>
                            <p className="text-[10px] text-slate-900 font-bold uppercase">(RNI:HRBIL/25/A2063)</p>
                            <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase tracking-tighter italic">Digitally Verified Identity</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Compact Professional Footer */}
          <div className="bg-white/95 backdrop-blur-sm border-t border-gray-100 mt-auto">
            <div className="max-w-7xl mx-auto px-4 py-8">
              <div className="flex flex-col items-center text-center gap-4">
                <img src="/images/iinsaf.png" alt="IINSAF Logo" className="h-10" />
                <p className="text-[10px] sm:text-xs text-slate-500 font-bold max-w-md uppercase tracking-widest leading-loose">
                  International Influencing News Social Media Advertisement Federation<br />
                  <span className="text-slate-400 text-[9px]">Verified Press Authority</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReporterGetIdCard;
